import { createContext, memo, useContext, type ComponentType } from 'react';
import { NodeToolbar, Position } from '@xyflow/react';
import { AlertTriangle, Hourglass, MousePointerClick, PauseCircle, Send } from 'lucide-react';
import type { FlowNodeProps } from '@/types/flow';
import type { FunnelStatsButton, FunnelStatsNode } from '@/types/funnel';
import { fmtDec, fmtInt } from '@/components/broadcast-details-modal/utils';
import NodeDetailCard from './NodeDetailCard';

interface FunnelStatsCtx {
    active: boolean;
    byNode: Map<number, FunnelStatsNode>;
    focusedId: number | null;
    funnelId: number | null;
    start: string;
    end: string;
    pageId: string | null;
    onClearFocus: () => void;
    onGoToNode: (nodeId: number) => void;
}

const Ctx = createContext<FunnelStatsCtx>({
    active: false, byNode: new Map(), focusedId: null, funnelId: null, start: '', end: '', pageId: null,
    onClearFocus: () => {}, onGoToNode: () => {},
});

export const FunnelStatsProvider = Ctx.Provider;

function useNodeStats(nodeId: number) {
    const ctx = useContext(Ctx);
    return { ...ctx, stats: ctx.byNode.get(nodeId), focused: ctx.focusedId === nodeId };
}

/**
 * Com o painel de desempenho aberto, cada node ganha uma faixa com os números
 * dele por cima. Envolve o componente do node em vez de alterá-lo, então os
 * 12 tipos ganham a faixa sem mexer em nenhum.
 */
export function withStats(Comp: ComponentType<FlowNodeProps>) {
    const Wrapped = (props: FlowNodeProps) => {
        const ctx = useNodeStats(props.data.id);
        const { active, stats, focused } = ctx;
        return (
            <div className={`relative rounded-lg transition-shadow ${focused ? 'ring-4 ring-amber-400 ring-offset-4 ring-offset-[#f8f9fb]' : ''}`}>
                {active && <NodeStatStrip stats={stats} />}
                <Comp {...props} />
                {active && focused && ctx.funnelId != null && (
                    <NodeToolbar isVisible position={Position.Right} offset={20} align="start">
                        <NodeDetailCard
                            funnelId={ctx.funnelId}
                            nodeId={props.data.id}
                            start={ctx.start}
                            end={ctx.end}
                            pageId={ctx.pageId}
                            onClose={ctx.onClearFocus}
                            onGoToNode={ctx.onGoToNode}
                        />
                    </NodeToolbar>
                )}
            </div>
        );
    };
    return memo(Wrapped);
}

function NodeStatStrip({ stats }: { stats?: FunnelStatsNode }) {
    const items: { icon: typeof Send; value: string; title: string; cls: string }[] = [];
    if (stats) {
        if (stats.sent || stats.failed) {
            items.push({ icon: Send, value: fmtInt(stats.sent), title: 'Enviados (aceitos pela Meta)', cls: 'text-blue-700' });
        }
        if (stats.failed) {
            items.push({ icon: AlertTriangle, value: `${fmtInt(stats.failed)} · ${fmtDec(100 - stats.success_rate)}%`, title: 'Falhas de envio', cls: 'text-red-600' });
        }
        if (stats.clicks) {
            items.push({ icon: MousePointerClick, value: `${fmtInt(stats.clicks)} · ${fmtDec(stats.ctr)}%`, title: 'Cliques · CTR', cls: 'text-emerald-700' });
        }
        if (stats.stuck) {
            items.push({ icon: PauseCircle, value: fmtInt(stats.stuck), title: `Parados aqui (do período) · ${fmtInt(stats.stuck_all)} desde sempre`, cls: 'text-orange-700' });
        }
        if (stats.waiting) {
            items.push({ icon: Hourglass, value: fmtInt(stats.waiting), title: 'Esperando o delay vencer', cls: 'text-amber-700' });
        }
    }

    return (
        <div className="absolute bottom-full left-0 mb-2 flex items-center gap-2 rounded-md bg-white/95 border border-gray-200 shadow-sm px-2 py-1 whitespace-nowrap pointer-events-none">
            {items.length ? items.map(({ icon: Icon, value, title, cls }) => (
                <span key={title} className={`flex items-center gap-1 text-[11px] font-semibold ${cls}`} title={title}>
                    <Icon className="w-3 h-3" />{value}
                </span>
            )) : <span className="text-[11px] text-gray-400">sem atividade no período</span>}
        </div>
    );
}

/** Selo de cliques e CTR dentro da linha do botão. Não renderiza nada com o painel fechado. */
export function ButtonStat({ nodeId, buttonId }: { nodeId: number; buttonId?: number }) {
    const { active, stats } = useNodeStats(nodeId);
    if (!active || buttonId == null) return null;
    const b: FunnelStatsButton | undefined = stats?.buttons.find((x) => x.button_id === buttonId);
    const clicks = b?.clicks ?? 0;
    return (
        <span
            className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-bold leading-none ${clicks ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-400'}`}
            title={b ? `${fmtInt(b.clicks)} cliques · ${fmtInt(b.unique_clickers)} leads · CTR ${fmtDec(b.ctr)}%` : 'Nenhum clique no período'}
        >
            {clicks ? `${fmtInt(clicks)} · ${fmtDec(b!.ctr)}%` : '0'}
        </span>
    );
}
